/**
 * One upload implementation for the whole app.
 *
 * WHY THIS EXISTS
 *
 * An audit of every file-upload surface on 2026-08-25 found 59 of them. ONE
 * reported real progress (creative-studio/DragDropZone). The other 58 sent the
 * file with fetch(), which cannot report upload progress — there is no event
 * for it and the body is consumed opaquely.
 *
 * Six of those 58 rendered a progress bar anyway, driven by files-completed
 * rather than bytes. For a single file that is 0% until the moment it finishes,
 * so the bar could not move. Reported from /dashboard/media as "the progress
 * bar barely moved, not sure if it even updated".
 *
 * Four tracked progress in a single object that was REPLACED on every call, so
 * starting a second upload while the first was in flight erased the first —
 * "the file name overwritten with the new file and 0/1".
 *
 * The fix is not 58 patches. It is this module plus the queue (now the
 * app-wide lib/uploadManager), adopted where progress actually matters. Presentation stays per-surface; only the
 * transport and the queue bookkeeping are shared.
 */

export type UploadStatus = 'pending' | 'uploading' | 'done' | 'error';

export type UploadItem = {
  id: string;
  name: string;
  /** Bytes. The denominator of any honest progress bar. */
  size: number;
  loaded: number;
  status: UploadStatus;
  error?: string;
  /** Machine-readable failure, for messages the UI translates itself. */
  code?: string;
  /** Whatever the caller needs back when the upload finishes. */
  result?: unknown;
};

export type ProgressFn = (loaded: number, total: number) => void;

/**
 * Is this upload failure worth another attempt (here, or via a different
 * transport)?
 *
 * This exists because the test used to be a bare `/network/.test(message)`,
 * duplicated in two files, and the stall watchdog below rejects with
 * "PUT stalled (no bytes for 120s)" — which does not contain "network". So the
 * watchdog added to rescue frozen overnight batches fired correctly and was
 * then classified as fatal by both the retry loop and the multipart
 * escalation: a stalled multi-GB upload failed permanently, with no retry and
 * no fallback, which is exactly what it was built to prevent.
 *
 * Matching on prose is what made that possible, so the transport now tags its
 * own errors and this reads the tag. The regex stays only as a fallback for
 * errors thrown by other layers.
 */
export function isTransientUploadError(err: unknown): boolean {
  if ((err as any)?.name === 'AbortError') return false;
  if ((err as any)?.transient === true) return true;
  return /network|stalled|timed out/i.test(String((err as any)?.message ?? ''));
}

/** Mark an error retryable so classification never depends on its wording. */
function transientError(message: string): Error {
  const e = new Error(message);
  (e as any).transient = true;
  return e;
}

/**
 * Sleep that a cancel can cut short.
 *
 * A plain setTimeout between retries ignores the abort signal, so "Cancel all"
 * during a backoff leaves the user watching a spinner for the rest of the
 * delay and then starts another attempt anyway.
 */
function abortableSleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new DOMException('Aborted', 'AbortError'));
    const t = setTimeout(() => { signal?.removeEventListener('abort', onAbort); resolve(); }, ms);
    function onAbort() { clearTimeout(t); reject(new DOMException('Aborted', 'AbortError')); }
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/** Stable-ish id without pulling in a uuid dependency. */
import { resumeStore, type ResumeRecord, type ResumeMeta } from './uploadResume';

export function uploadId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * XMLHttpRequest, deliberately, and only here.
 *
 * fetch() has no upload-progress event. Until `ReadableStream` request bodies
 * are universally available (they need HTTP/2 and duplex: 'half', and Safari
 * is still short of it), XHR is the only way to observe bytes leaving the
 * browser. This is the one place in the app where reaching for XHR is a
 * decision rather than a leftover — everything else should keep using fetch.
 */
function xhrSend(
  method: 'PUT' | 'POST',
  url: string,
  body: XMLHttpRequestBodyInit,
  opts: { headers?: Record<string, string>; onProgress?: ProgressFn; signal?: AbortSignal; idleTimeoutMs?: number } = {},
): Promise<{ status: number; text: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    for (const [k, v] of Object.entries(opts.headers || {})) xhr.setRequestHeader(k, v);

    // Stall watchdog. XHR has no default timeout, so a request on a dead
    // connection (laptop sleep mid-upload, network drop) hangs FOREVER with
    // no error — measured in prod as an overnight 80GB batch freezing
    // silently at 01:00 local. If no bytes move for idleTimeoutMs we abort
    // and reject as a TRANSIENT error (not AbortError), so the caller's
    // retry loop treats it like any other blip instead of a user cancel.
    // Long enough that a slow-but-alive uplink never trips it.
    // The tag is load-bearing: this rejection reads "stalled", and while
    // callers classified by searching the message for "network" it was
    // silently treated as fatal.
    const idleMs = opts.idleTimeoutMs ?? 120_000;
    let stalled = false;
    let idleTimer: ReturnType<typeof setTimeout> | undefined;
    const armIdle = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => { stalled = true; xhr.abort(); }, idleMs);
    };
    const disarmIdle = () => { if (idleTimer) clearTimeout(idleTimer); };

    xhr.upload.onprogress = (e) => {
      armIdle();
      // lengthComputable is false when the body is chunked or compressed.
      // Reporting nothing beats reporting a percentage that is made up.
      if (e.lengthComputable) opts.onProgress?.(e.loaded, e.total);
    };
    xhr.onprogress = () => armIdle(); // response bytes count as liveness too
    xhr.onload = () => { disarmIdle(); resolve({ status: xhr.status, text: xhr.responseText }); };
    xhr.onerror = () => { disarmIdle(); reject(transientError(`${method} failed (network)`)); };
    xhr.ontimeout = () => { disarmIdle(); reject(transientError(`${method} timed out`)); };
    xhr.onabort = () => {
      disarmIdle();
      if (stalled) reject(transientError(`${method} stalled (no bytes for ${Math.round(idleMs / 1000)}s)`));
      else reject(new DOMException('Aborted', 'AbortError'));
    };

    if (opts.signal) {
      // MUST reject, not return. xhr.abort() in the OPENED-but-not-sent state
      // fires no abort event, so a bare `return` here leaves this promise
      // permanently unsettled: uploadOne never returns, its queue worker never
      // advances, and the whole batch hangs with the strip spinning forever.
      // Reachable whenever a cancel lands during a retry backoff — a window
      // that got wider once stalls and timeouts became retryable.
      if (opts.signal.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      opts.signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }
    armIdle();
    xhr.send(body);
  });
}

/**
 * Same-origin fallback when the direct R2 PUT is unreachable.
 *
 * Measured in prod: some browser environments (extensions, shields) block
 * <account>.r2.cloudflarestorage.com outright — every attempt fails with
 * ERR_ACCESS_DENIED in the same second. Nothing signed can fix a domain the
 * client refuses to reach; the same-origin API host always works. Capped by
 * the Worker request-body limit, so it is a FALLBACK, not the default.
 */
export async function putViaProxy(
  apiBase: string,
  token: string,
  r2Key: string,
  file: File,
  opts: { contentType?: string; onProgress?: ProgressFn; signal?: AbortSignal } = {},
): Promise<void> {
  const url = `${apiBase}/media-library/upload-proxy?key=${encodeURIComponent(r2Key)}`;
  const { status, text } = await xhrSend('PUT', url, file, {
    headers: {
      Authorization: `Bearer ${token}`,
      'x-upload-content-type': opts.contentType || file.type || 'application/octet-stream',
    },
    onProgress: opts.onProgress,
    signal: opts.signal,
  });
  if (status < 200 || status >= 300) {
    let msg = `Upload failed (${status})`;
    try { msg = JSON.parse(text)?.error || msg; } catch { /* keep default */ }
    throw new Error(msg);
  }
}

/**
 * Chunk retry budget. Multipart is the LAST transport, reached only after the
 * direct PUT and the proxy already failed, so it is the one that has to outlast
 * a blip. It used to give up after 1.5 s + 3 s of backoff: on 2026-09-25 a
 * ~10-20 s interruption failed all five files in flight, each of which uploaded
 * fine two minutes later. 2+4+8+16 s rides out ~30 s; a chunk that is still
 * failing after that is not a blip.
 */
const PART_BACKOFF_MS = [2_000, 4_000, 8_000, 16_000];
const PART_MAX_ATTEMPTS = PART_BACKOFF_MS.length + 1;

/**
 * Can the browser still read this file's bytes?
 *
 * XHR reports "the file changed or vanished on disk" (Chrome's
 * ERR_UPLOAD_FILE_CHANGED, a drive or sync folder going away) with the same
 * bare onerror as a network drop, so a failed upload cannot tell the two apart
 * from the error alone. Reading one byte can: a network failure leaves the
 * file readable, a source-side failure does not — and retrying the same File
 * object is then pointless; the user has to add it again.
 */
export async function fileIsReadable(file: Blob): Promise<boolean> {
  try {
    await file.slice(0, 1).arrayBuffer();
    return true;
  } catch {
    return false;
  }
}

/**
 * Chunked multipart upload through our own origin — last-resort fallback
 * that works in every environment (POST-shaped requests, which even the
 * strictest security stacks allow) at every size (each 50 MB chunk is far
 * under the Worker body cap; R2 assembles them server-side). A failed
 * chunk retries alone instead of restarting the whole file.
 */
export async function putMultipart(
  apiBase: string,
  token: string,
  r2Key: string,
  file: File,
  opts: {
    contentType?: string; onProgress?: ProgressFn; signal?: AbortSignal; resume?: ResumeRecord;
    /** Stored on the resume record so a resume after a reload completes into the same place. */
    meta?: ResumeMeta;
  } = {},
): Promise<void> {
  // Fixed for the life of the upload: R2 needs every part but the last to be
  // the same size, so a resume reuses the size it started with.
  const CHUNK = opts.resume?.chunkSize || 50 * 1024 * 1024; // R2 min part is 5 MiB (except last)
  const auth = { Authorization: `Bearer ${token}` };
  const qs = (extra: string) => `${apiBase}/media-library/upload-mpu-${extra}`;
  const contentType = opts.contentType || file.type || 'application/octet-stream';

  // Resume: reuse the saved uploadId + already-completed parts. Otherwise
  // create a fresh multipart upload.
  let uploadIdStr: string;
  const donePartNums = new Set<number>();
  const parts: Array<{ partNumber: number; etag: string }> = [];
  if (opts.resume && resumeStore.matches(opts.resume, file)) {
    uploadIdStr = opts.resume.uploadId;
    for (const p of opts.resume.parts) { donePartNums.add(p.partNumber); parts.push(p); }
  } else {
    const createRes = await xhrSend('POST', `${qs('create')}?key=${encodeURIComponent(r2Key)}&size=${file.size}`, new Blob([]), {
      headers: { ...auth, 'x-upload-content-type': contentType },
      signal: opts.signal,
    });
    let create: any = {};
    try { create = JSON.parse(createRes.text || '{}'); } catch { /* non-JSON error page */ }
    if (!create.success) {
      const err = new Error(create.error || `Could not start upload (${createRes.status})`);
      // Nothing was sent yet, so a caller may still fall back to a single PUT.
      (err as any).stage = 'create';
      throw err;
    }
    uploadIdStr = create.uploadId;
  }
  const create = { uploadId: uploadIdStr, success: true };

  // Persist progress so a crash mid-upload is resumable. Extra fields from an
  // older write (folder, handle, ...) ride along unless meta replaces them.
  const { parts: _oldParts, updatedAt: _oldAt, ...carried } = opts.resume || ({} as Partial<ResumeRecord>);
  // Nothing after a cancel: the canceller deletes the record, and a part that
  // lands a moment later must not write it back.
  const persist = () => (opts.signal?.aborted ? Promise.resolve() : resumeStore.put({
    ...carried, ...(opts.meta || {}),
    r2Key, uploadId: uploadIdStr, fileName: file.name, fileSize: file.size,
    fileLastModified: file.lastModified, contentType, chunkSize: CHUNK,
    queued: false, parts: [...parts], updatedAt: Date.now(),
  }).catch(() => {}));
  // Written now, not after the first part: a reload during the first 50 MB
  // used to leave nothing to resume.
  await persist();

  // Plan the parts up front so we can upload several CONCURRENTLY. R2 assigns
  // each part by its number, not arrival order, so out-of-order completion is
  // fine — we sort by partNumber before calling complete.
  const total = file.size;
  const plan: Array<{ n: number; offset: number; size: number }> = [];
  for (let offset = 0, n = 1; offset < total; offset += CHUNK, n++) {
    plan.push({ n, offset, size: Math.min(CHUNK, total - offset) });
  }
  // A pathological 0-part plan (0-byte file) can't multipart — R2 needs >=1
  // part. Callers already guard 0-byte files, but fail loudly if one slips.
  if (plan.length === 0) throw new Error('Nothing to upload (empty file)');

  // Aggregate progress across concurrent chunks: track bytes per part and
  // report their sum, so the bar reflects real total throughput rather than
  // jumping as each part finishes.
  const loadedByPart = new Map<number, number>();
  const reportProgress = () => {
    if (!opts.onProgress) return;
    let sum = 0;
    for (const v of loadedByPart.values()) sum += v;
    opts.onProgress(Math.min(sum, total), total);
  };

  // Pre-credit already-completed parts to the progress bar on resume.
  for (const item of plan) {
    if (donePartNums.has(item.n)) loadedByPart.set(item.n, item.size);
  }
  reportProgress();

  const uploadPart = async (item: { n: number; offset: number; size: number }): Promise<void> => {
    if (donePartNums.has(item.n)) return; // resumed: already uploaded
    const slice = file.slice(item.offset, item.offset + item.size);
    let lastErr: any = null;
    for (let attempt = 1; attempt <= PART_MAX_ATTEMPTS; attempt++) {
      try {
        const res = await xhrSend('POST',
          `${qs('part')}?key=${encodeURIComponent(r2Key)}&uploadId=${encodeURIComponent(create.uploadId)}&part=${item.n}`,
          slice, {
            headers: auth,
            signal: opts.signal,
            onProgress: (loaded) => { loadedByPart.set(item.n, loaded); reportProgress(); },
          });
        const j = JSON.parse(res.text || '{}');
        if (!j.success) throw new Error(j.error || `Chunk ${item.n} failed (${res.status})`);
        parts.push({ partNumber: item.n, etag: j.etag });
        loadedByPart.set(item.n, item.size); // count completed part in full
        reportProgress();
        void persist();
        return;
      } catch (e: any) {
        if (e?.name === 'AbortError') throw e;
        lastErr = e;
        loadedByPart.set(item.n, 0); // failed attempt: don't credit partial bytes
        if (attempt < PART_MAX_ATTEMPTS) await abortableSleep(PART_BACKOFF_MS[attempt - 1], opts.signal);
      }
    }
    throw lastErr;
  };

  // Bounded concurrency: CONCURRENCY parts in flight at once. Workers pull
  // from a shared cursor so a slow chunk doesn't stall the others. First
  // failure aborts the whole file (the error propagates out of Promise.all).
  const CONCURRENCY = 4;
  let cursor = 0;
  const worker = async (): Promise<void> => {
    while (cursor < plan.length) {
      const item = plan[cursor++];
      await uploadPart(item);
    }
  };
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, plan.length) }, () => worker()));

  parts.sort((a, b) => a.partNumber - b.partNumber);

  const doneRes = await xhrSend('POST', qs('complete'), JSON.stringify({ key: r2Key, uploadId: create.uploadId, parts }), {
    headers: { ...auth, 'Content-Type': 'application/json' },
    signal: opts.signal,
  });
  const done = JSON.parse(doneRes.text || '{}');
  if (!done.success) throw new Error(done.error || `Could not finish upload (${doneRes.status})`);
  await resumeStore.delete(r2Key).catch(() => {});
}

/** PUT a file straight to a presigned URL (the R2 path), with byte progress. */
export async function putWithProgress(
  url: string,
  file: File,
  opts: {
    onProgress?: ProgressFn;
    signal?: AbortSignal;
    contentType?: string;
    /** Seconds the presigned URL stays valid, from upload-init's `expiresIn`. */
    expiresInSec?: number;
  } = {},
): Promise<void> {
  // The presigned URL is SIGNED for a specific Content-Type; the header must
  // match it exactly. contentType carries the server-resolved type from
  // upload-init — for an extensionless file, file.type is '' so no header
  // was sent while the signature expected octet-stream → R2 403 (shown by
  // XHR as a network error).
  const ct = opts.contentType || file.type;
  // Retry transient failures. A multi-minute single-socket PUT (files go up
  // to 5 GB in ONE request) dies to any connection reset, and a directory
  // drop runs several at once — one blip failed the file permanently with
  // "PUT failed (network)". Network errors and 5xx retry with backoff;
  // 4xx (signature, size, expiry) and user aborts do not.
  const MAX_TRIES = 3;
  // The signature dies on a wall clock, not on attempts. A multi-GB PUT
  // routinely runs longer than the 5-minute TTL, so retrying the SAME url
  // after a long first attempt is a guaranteed 403 — it burns minutes and
  // then reports a permission error for what was really a network blip.
  // Once the URL is dead, give up here and let the caller escalate to
  // multipart (authenticated per-chunk, so it has no expiry to outlive).
  const deadline = opts.expiresInSec ? Date.now() + opts.expiresInSec * 1000 : null;
  const urlExpired = () => deadline !== null && Date.now() >= deadline;

  for (let attempt = 1; ; attempt++) {
    try {
      const { status } = await xhrSend('PUT', url, file, {
        headers: ct ? { 'Content-Type': ct } : {},
        onProgress: opts.onProgress,
        signal: opts.signal,
      });
      if (status >= 200 && status < 300) return;
      if (status >= 500 && attempt < MAX_TRIES && !urlExpired()) {
        await abortableSleep(attempt * 1500, opts.signal);
        continue;
      }
      // Past the deadline the signature is dead, so neither a 403 nor a 5xx is
      // a verdict on the request — both mean "this URL is finished". Tagged
      // transient so the caller escalates to multipart instead of failing the
      // file. The 5xx case has to be here too: without it a post-deadline 500
      // falls through as a plain fatal Error.
      if (urlExpired() && (status === 403 || status >= 500)) {
        throw transientError('Upload URL expired mid-transfer');
      }
      throw new Error(`Upload failed (${status})`);
    } catch (e: any) {
      if (e?.name === 'AbortError') throw e;
      if (isTransientUploadError(e) && attempt < MAX_TRIES && !urlExpired()) {
        console.warn(`[upload] transient PUT failure (attempt ${attempt}/${MAX_TRIES}), retrying:`, e?.message);
        await abortableSleep(attempt * 1500, opts.signal);
        continue;
      }
      throw e;
    }
  }
}

/**
 * POST multipart/form-data with byte progress — for the endpoints that take a
 * FormData body rather than handing back a presigned URL.
 *
 * Content-Type is deliberately NOT set: the browser must add the multipart
 * boundary itself, and setting it by hand produces a body the server cannot
 * parse.
 */
export async function postFormWithProgress<T = unknown>(
  url: string,
  form: FormData,
  opts: { token?: string | null; onProgress?: ProgressFn; signal?: AbortSignal } = {},
): Promise<T> {
  const { status, text } = await xhrSend('POST', url, form, {
    headers: opts.token ? { Authorization: `Bearer ${opts.token}` } : {},
    onProgress: opts.onProgress,
    signal: opts.signal,
  });
  let parsed: any = null;
  try { parsed = text ? JSON.parse(text) : null; } catch { /* non-JSON body */ }
  if (status < 200 || status >= 300) {
    throw new Error(parsed?.error || `Upload failed (${status})`);
  }
  return parsed as T;
}

/** Aggregate figures for a queue. Bytes, never files-completed. */
export function queueStats(items: UploadItem[]) {
  const totalBytes = items.reduce((n, u) => n + u.size, 0);
  const loadedBytes = items.reduce((n, u) => n + (u.status === 'done' ? u.size : u.loaded), 0);
  const done = items.filter((u) => u.status === 'done').length;
  const failed = items.filter((u) => u.status === 'error').length;
  return {
    totalBytes,
    loadedBytes,
    done,
    failed,
    total: items.length,
    settled: items.length > 0 && items.every((u) => u.status === 'done' || u.status === 'error'),
    // Percentage of BYTES. With files-completed a single upload could only
    // ever read 0% or 100%, which is what made the old bar look frozen.
    pct: totalBytes > 0 ? Math.round((loadedBytes / totalBytes) * 100) : 0,
  };
}

/**
 * Display order for a queue: failures first, then in-flight, then waiting,
 * then done.
 *
 * Lives here rather than in the strip because it is the rule that decides
 * whether a user can SEE a failure, and it has to hold for a 93-file batch,
 * not just a demo of three. The strip renders the first N entries; in queue
 * order, the five failures in a 93-file drop were essentially never among
 * them, so the user was told "5 failed" and shown none of them.
 *
 * Stable within a rank (Array.prototype.sort is stable), so files keep their
 * queue order inside each group and rows do not shuffle as progress ticks.
 */
const DISPLAY_RANK: Record<UploadStatus, number> = { error: 0, uploading: 1, pending: 2, done: 3 };

export function orderForDisplay(items: UploadItem[]): UploadItem[] {
  return [...items].sort((a, b) => DISPLAY_RANK[a.status] - DISPLAY_RANK[b.status]);
}

export function formatBytes(b: number): string {
  if (b >= 1_073_741_824) return `${(b / 1_073_741_824).toFixed(1)} GB`;
  if (b >= 1_048_576) return `${(b / 1_048_576).toFixed(1)} MB`;
  if (b >= 1024) return `${Math.round(b / 1024)} KB`;
  return `${b} B`;
}
